import React, { useState, useMemo, useEffect }  from 'react';
import { useMsal, useAccount } from "@azure/msal-react";
import { MaterialReactTable } from 'material-react-table';
import jQuery from 'jquery';
import * as Utils from '../lib/utils';
import { protectedResources } from "../config/authConfig";
import CONFIG from '../config';

window.jQuery = jQuery;
require("jsgrid");


const debounce = function(fn, ms) {
  let timer
  return _ => {
    clearTimeout(timer)
    timer = setTimeout(_ => {
      timer = null
      fn.apply(this, arguments)
    }, ms)
  };
}

const Budget = () => {
    const { instance, accounts, inProgress } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [dimensions, setDimensions] = useState({ 
      height: window.innerHeight,
      width: window.innerWidth
    });
    
    const [tableData, setTableData] = useState([]);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);    
    const [rowCount, setRowCount] = useState(0);

    const [compensationCycles, setCompensationCycles] = useState([]);
    const [selectedEffectiveDate, setSelectedEffectiveDate] = useState("");

    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadEffectiveDate, setUploadEffectiveDate] = useState("");

    const urlParams = Utils.getParams();
    const effectiveDate = urlParams.effectiveDate;
    const adjustmentUuid = urlParams.adjustmentUuid;
    const adjustmentStatus = urlParams.status;
    // console.log("urlParams", urlParams);

    const fetchCompensationCycles = async () => {
      try {
        // Fetch all compensation cycle effective dates from pending, review, approved, and closed
        const [pending, review, approved, closed] = await Promise.all([
          Utils.getData('/api/pending', instance, account),
          Utils.getData('/api/review', instance, account),
          Utils.getData('/api/approved', instance, account),
          Utils.getData('/api/closed', instance, account),
        ]);

        // Combine all effective dates and deduplicate
        const allDates = new Set();
        [pending, review, approved, closed].forEach((dataSet) => {
          if (Array.isArray(dataSet)) {
            dataSet.forEach((item) => {
              if (item.effective_date) {
                allDates.add(item.effective_date);
              }
            });
          }
        });

        // Sort descending
        const sortedDates = Array.from(allDates).sort((a, b) => b.localeCompare(a));
        setCompensationCycles(sortedDates);

        // If there's a URL param, use it; otherwise default to the first available cycle
        if (effectiveDate) {
          setSelectedEffectiveDate(effectiveDate);
        } else if (sortedDates.length > 0) {
          setSelectedEffectiveDate(sortedDates[0]);
        }
      } catch (error) {
        console.error("Error fetching compensation cycles:", error);
      }
    };

    const fetchData = async (dateFilter) => {
      if (!tableData.length) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }

      try {
        const dateParam = dateFilter || selectedEffectiveDate;
        const url = dateParam 
          ? '/api/budget?effective_date=' + dateParam
          : '/api/budget';

        const jsonResponse = await Utils.getData(
          url, 
          instance, 
          account
        );

        setTableData(jsonResponse);
        setRowCount(jsonResponse.length);
      } catch (error) {
        setIsError(true);
        console.error(error);
        return;
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    };

    useEffect(() => {
      fetchCompensationCycles();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (selectedEffectiveDate) {
        fetchData(selectedEffectiveDate);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEffectiveDate]);

    useEffect(() => {
      const debouncedHandleResize = debounce(function handleResize() {
        setDimensions({
          height: window.innerHeight,
          width: window.innerWidth
        })
      }, 500)

      //binds a resize listener for a debounced (less frequent) resize effect
      window.addEventListener('resize', debouncedHandleResize);
      
      //unbinds listener to prevent memory leaks
      return _ => {
        window.removeEventListener('resize', debouncedHandleResize)   
      };
    });

    const triggerLink = (linkId, noToken)=>{
      instance.acquireTokenSilent({
        scopes: protectedResources["apiCompensation"].scopes,
        account: account
      }).then((msalAuthorization)=>{
        //add the token
        let selectedLink = document.getElementById(linkId).href
        if(!noToken){
          selectedLink = selectedLink + "&token=" + (msalAuthorization ? msalAuthorization.accessToken : "")
        } 
        
        //set the link handler
        document.getElementById("linkHandler").href = selectedLink;
        
        //click the link
        document.getElementById("linkHandler").click()
      });
    }  
    

    //should be memoized or stable
    const columns = useMemo(
      () => [
        {
          accessorKey: 'view_employee',
          header: 'View Adjustments',
          Cell: ({ cell, row }) => {
            // console.log("cell", cell, row);
            return <div>
              <a 
                href={'/compensation?effectiveDate='+row.original.effective_date.substring(0,10)+'&managerHrisId=' + row.original.employee_hris_id}>
                  View Adjustments
                </a>
              </div>
          },
          enableEditing: false,
          columnDefType: 'display',
          enablePinning: false
        },
        {
          accessorFn: (row) => `${Utils.maskDateAs("mdy", new Date(row.effective_date))}`,
          accessorKey: 'effective_date',
          header: 'Effective Date',
          enableEditing: false,
        },
        {
          accessorFn: (row) => `${Utils.decodeHTML(row.first_name)} ${Utils.decodeHTML(row.last_name)}`,
          accessorKey: 'manager_name',
          header: 'Manager Name',
          enableEditing: false
        },
        {
          accessorFn: (row) => {
            // console.log("row", row)
            let employeeListing = row.adjustment_employee_list;
            employeeListing.sort();
            return Utils.decodeHTML(employeeListing.join(", "));
          },
          accessorKey: 'employee_list',
          header: 'Employees',
          enableEditing: false,
        },        
        {
          accessorKey: 'budget',
          header: 'Budget Percentage',
          enableEditing: true,
        },
        {
          accessorKey: 'adjustment_reports',
          header: '# of Requests',
          enableEditing: false,
        },
      ],
      [],
    );


    //saves the manager to the DB and updates the client view
    function saveBudget(action, originalRow, employee){
      // console.log("action", action, "originalRow", originalRow, "employee", employee);
      let data = {
        manager_id: originalRow.id,
        adjustment_uuid: "",
        manager_budget: employee.budget,
        effective_date: originalRow.effective_date.substring(0,10)
      };

      Utils.postData( "/api/budget", data, instance, account ).then((data) => { 
        return false;
      });

      return false;
    }


    const uploadManagerBudgets = () => {
      if (!selectedFile) {
        alert("Please select a file to upload.");
        return false;
      }
      if (!uploadEffectiveDate) {
        alert("Please select a compensation cycle to apply the budget to.");
        return false;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('effective_date', uploadEffectiveDate);
  
      Utils.postFile(
        "/api/budget/upload", 
        formData,
        instance,
        account
      ).then((result) => {
        alert("Budgets uploaded successfully.");
        setSelectedFile(null);
        fetchData(selectedEffectiveDate);
      }).catch((error) => {
        console.error("Upload error:", error);
        alert("There was an error uploading budgets.");
      });
      return false;
    }    


    const handleSaveRow = async ({ exitEditingMode, row, values, table }) => {
      // let data = table.getState().tableData; //deprecated
      let data = tableData;
      data[row.index].budget = values.budget;
      setTableData([...data]);
      saveBudget("edit", row.original, values);
      exitEditingMode();
    };
    
    const tableHeightValue =  window.innerHeight > 183 ? window.innerHeight - 183 : 300;
    const tableHeightCSS = tableHeightValue + "px";

    return <div>

      <MaterialReactTable 
        renderTopToolbarCustomActions={({ table }) => {
          return <div>
            <span className="sectionTableHeader">Manager Budgets</span>
            &nbsp;&nbsp;&nbsp;
            <a 
                id={"downloadBudgets"}
                href={ CONFIG.API_HOST + "/api/budget?d=true&f=xl" + (selectedEffectiveDate ? "&effective_date=" + selectedEffectiveDate : "") }
                onClick={(event)=>{
                  event.preventDefault()
                  triggerLink("downloadBudgets");
                  return false;
                }}
            >
              Download Budgets
            </a> 
  
            <a href="#" id="linkHandler" style={{ visibility:"hidden" }}></a>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;

            <div style={{ display:"inline-block" }}>
              <label htmlFor="cycleSelector"><strong>Compensation Cycle: </strong>&nbsp;</label>
              <select 
                id="cycleSelector"
                value={selectedEffectiveDate} 
                onChange={(e) => {
                  setSelectedEffectiveDate(e.target.value);
                }}
              >
                {compensationCycles.length === 0 && <option value="">No cycles available</option>}
                {compensationCycles.map((date) => (
                  <option key={date} value={date}>
                    {Utils.maskDateAs("mdy", new Date(date + " 12:00:00"))}
                  </option>
                ))}
              </select>
            </div>
            &nbsp;&nbsp;&nbsp;

            <div style={{ display:"inline-block" }}>
              <label><strong>Upload Budgets: </strong>&nbsp;</label>
              <select 
                value={uploadEffectiveDate} 
                onChange={(e) => setUploadEffectiveDate(e.target.value)}
              >
                <option value="">-- Select Cycle --</option>
                {compensationCycles.map((date) => (
                  <option key={date} value={date}>
                    {Utils.maskDateAs("mdy", new Date(date + " 12:00:00"))}
                  </option>
                ))}
              </select>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <input 
                type="file" 
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
              <button className="btn btn-primary" onClick={uploadManagerBudgets}>Upload</button>
            </div>
          </div>
        }}
        columns={columns} 
        data={tableData}
        globalFilterFn="contains" 
        enableStickyHeader
        muiToolbarAlertBannerProps={
          isError
            ? {
                color: 'error',
                children: 'Error loading data',
              }
            : undefined
        }        
        muiTableContainerProps={{ sx: { height: tableHeightCSS } }}
        rowCount={rowCount}
        enableEditing
        editDisplayMode="row"
        onEditingRowSave={handleSaveRow}        
        state={{
          isLoading,
          showAlertBanner: isError,
          showProgressBars: isRefetching,
          tableData: tableData
        }}
      />
    </div>;
};

export default Budget;
